const VERSION_URL = '/_version.json';
const VERSION_CHECK_INTERVAL_MS = 5 * 60 * 1000;
const RELOAD_MESSAGE = 'A new version is available. Reload to continue.';

let reloadPromptShown = false;
let lastVersionCheckAt = 0;
let versionCheckPromise = null;

function getErrorText(error) {
  if (!error) {
    return '';
  }
  if (typeof error === 'string') {
    return error;
  }
  const parts = [error.name, error.message, error.stack].filter(Boolean);
  return parts.join(' ');
}

function getFailedAssetUrl(error) {
  const target = error && error.target;
  if (!target || !target.tagName) {
    return '';
  }
  const tagName = target.tagName.toLowerCase();
  if (tagName === 'script') {
    return target.src || '';
  }
  if (tagName === 'link') {
    return target.href || '';
  }
  return '';
}

export function isStaleAssetLoadError(error) {
  const text = getErrorText(error).toLowerCase();
  if (
    text.includes('chunkloaderror') ||
    text.includes('loading chunk') ||
    text.includes('loading css chunk') ||
    text.includes('failed to fetch dynamically imported module') ||
    text.includes('importing a module script failed')
  ) {
    return true;
  }

  const failedAssetUrl = getFailedAssetUrl(error).toLowerCase();
  return !!failedAssetUrl && failedAssetUrl.includes('/static/') && /\.(js|css)(\?|$)/.test(failedAssetUrl);
}

function reloadPage() {
  try {
    window.location.href = window.location.href; // this should reload without cache
  } catch (e) {
    try {
      window.location = window.location.href; // a fallback
    } catch (e) {
      window.location.reload(); // final fallback to regular reload
    }
  }
}

function showReloadPrompt(app, promptReload) {
  if (reloadPromptShown) {
    return;
  }
  reloadPromptShown = true;

  if (promptReload) {
    promptReload(reloadPage);
    return;
  }

  const toaster = app && app.$toasted;
  if (toaster) {
    toaster.show(RELOAD_MESSAGE, {
      duration: null,
      action: {
        text: 'Reload',
        onClick: (event, toastObject) => {
          if (toastObject) {
            toastObject.goAway(0);
          }
          reloadPage();
        },
      },
    });
    return;
  }

  if (window.confirm(RELOAD_MESSAGE)) {
    reloadPage();
  }
}

function normalizeLatestBuildId(versionInfo) {
  return versionInfo && versionInfo.buildId ? versionInfo.buildId : null;
}

export function createStaleBuildRecovery(options) {
  const router = options.router;
  const app = options.app;
  const currentBuildId = options.currentBuildId;
  const fetchImpl = options.fetchImpl || (window.fetch ? window.fetch.bind(window) : null);
  const versionUrl = options.versionUrl || VERSION_URL;
  const minCheckIntervalMs = options.minCheckIntervalMs || VERSION_CHECK_INTERVAL_MS;
  const promptReload = options.promptReload;

  function promptForReload() {
    showReloadPrompt(app, promptReload);
  }

  function checkLatestBuild(force) {
    if (!currentBuildId || !fetchImpl) {
      return Promise.resolve(false);
    }

    const now = Date.now();
    if (!force && now - lastVersionCheckAt < minCheckIntervalMs) {
      return Promise.resolve(false);
    }
    if (versionCheckPromise) {
      return versionCheckPromise;
    }

    lastVersionCheckAt = now;
    versionCheckPromise = fetchImpl(`${versionUrl}?ts=${now}`, {cache: 'no-store'})
      .then(response => {
        if (!response || !response.ok) {
          return false;
        }
        return response.json();
      })
      .then(versionInfo => {
        const latestBuildId = normalizeLatestBuildId(versionInfo);
        if (latestBuildId && latestBuildId !== currentBuildId) {
          promptForReload();
          return true;
        }
        return false;
      })
      .catch(() => false)
      .then(result => {
        versionCheckPromise = null;
        return result;
      });

    return versionCheckPromise;
  }

  function handleError(error) {
    if (isStaleAssetLoadError(error)) {
      promptForReload();
      return true;
    }
    return false;
  }

  function install() {
    if (router && router.onError) {
      router.onError(handleError);
    }
    if (router && router.afterEach) {
      router.afterEach(() => checkLatestBuild(false));
    }
    window.addEventListener('unhandledrejection', event => handleError(event.reason));
    window.addEventListener(
      'error',
      event => {
        handleError(event.error || event);
      },
      true
    );
    window.addEventListener('focus', () => checkLatestBuild(false));
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        checkLatestBuild(false);
      }
    });

    checkLatestBuild(true);
  }

  return {
    checkLatestBuild,
    handleError,
    install,
  };
}

export function installStaleBuildRecovery(options) {
  const recovery = createStaleBuildRecovery(options);
  recovery.install();
  return recovery;
}

export function resetStaleBuildRecoveryState() {
  reloadPromptShown = false;
  lastVersionCheckAt = 0;
  versionCheckPromise = null;
}
